import { Loader2, Mic } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { MicFillIcon } from "@/components/media/mic-fill-icon"
import { MediaRecordGlyph } from "@/components/media/media-record-glyph"
import { Button, Checkbox } from "@/components/ui/primitives"
import { SelectDropdown } from "@/components/ui/select-dropdown"
import { VoicePicker } from "@/components/voices/voice-picker"
import { useAudioInputPicker } from "@/hooks/use-audio-input-picker"
import { useAudioRecorder } from "@/hooks/use-audio-recorder"
import { useTranslation } from "@/hooks/use-translation"
import { buildAudioInputOptions, SYSTEM_AUDIO_INPUT_VALUE } from "@/lib/audio-inputs"
import { cn } from "@/lib/utils"
import type { VoiceSelection } from "@/lib/voices-api"
import type { TimelineVoiceOption } from "./types"
import { AUDIO_COMPOSE_FIELD, AudioComposeDialog } from "./audio-compose-dialog"
import { AudioTakeTimeline, type TakeVideoSync } from "./audio-take-timeline"

const MAX_RECORD_SECONDS = 30

function formatClock(totalSeconds: number) {
  const safe = Math.max(0, Math.floor(totalSeconds))
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`
}

type AudioRecordDialogProps = {
  projectId: number
  voices: TimelineVoiceOption[]
  stsProviderKinds: string[]
  busy: boolean
  portalTarget: HTMLElement | null
  onClose: () => void
  /** maxDurationMs — сколько мс шот проиграл за дубль; null, если галочка выключена. */
  onSave: (file: File, voice: VoiceSelection | null, maxDurationMs: number | null) => Promise<boolean>
  /** Пауза на время отсчёта, чтобы картинка не уехала вперёд относительно микрофона. */
  onPauseForDub?: () => void
  /** Перемотка на 0, как только пошёл обратный отсчёт (галочка включена). */
  onRewindForDub?: () => void
  /** Старт шота с начала в момент, когда запись реально началась. Плейхед уже на 0 после отсчёта. */
  onPlayForDub?: () => void
  /** Вернуть звук шота: галочку сняли или новый дубль пишется без видео. */
  onReleaseDubSilence?: () => void
  /** Плейхед шота для прослушивания дубля, снятого вместе с видео. */
  shotSync?: TakeVideoSync
  /** Плейхед сразу после play(). */
  onReadPlayhead?: () => number
  /** Идёт ли воспроизведение шота. Падение в false при включённой галочке останавливает запись. */
  shotPlaying?: boolean
}

export function AudioRecordDialog({
  projectId,
  voices,
  stsProviderKinds,
  busy,
  portalTarget,
  onClose,
  onSave,
  onPauseForDub,
  onRewindForDub,
  onPlayForDub,
  onReleaseDubSilence,
  shotSync,
  onReadPlayhead,
  shotPlaying = false,
}: AudioRecordDialogProps) {
  const { t } = useTranslation(["shot"])
  const picker = useAudioInputPicker({
    onDeviceUnavailable: () => toast.warning(t("timeline.microphoneUnavailable")),
  })
  const stsAvailable = stsProviderKinds.length > 0
  const [voice, setVoice] = useState<VoiceSelection | null>(null)
  const [playWithVideo, setPlayWithVideo] = useState(false)
  const [take, setTake] = useState<File | null>(null)
  const [takeUrl, setTakeUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!take) {
      setTakeUrl(null)
      return
    }
    const url = URL.createObjectURL(take)
    setTakeUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [take])

  const inputOptions = useMemo(() => {
    const listed = buildAudioInputOptions({
      devices: picker.devices,
      value: picker.value,
      pendingLabel: picker.pendingLabel,
      defaultDeviceId: picker.defaultDeviceId,
      labels: {
        defaultInput: t("timeline.defaultMicrophone"),
        defaultInputNamed: (name) => t("timeline.defaultInputNamed", { name }),
        microphoneN: (n) => t("timeline.microphoneN", { n }),
      },
    })
    return [
      ...listed.map((option, i) => ({ ...option, separatorAfter: i === listed.length - 1 })),
      { value: SYSTEM_AUDIO_INPUT_VALUE, label: t("timeline.systemAudio") },
    ]
  }, [picker.devices, picker.value, picker.pendingLabel, picker.defaultDeviceId, t])

  const shotWasPlayingRef = useRef(shotPlaying)
  /** Плейхед в момент старта дубля. null — галочка выключена. */
  const dubStartMsRef = useRef<number | null>(null)
  /** Длина проигранного куска. Первый стоп (видео или кнопка) фиксирует её навсегда. */
  const dubLimitMsRef = useRef<number | null>(null)
  const onReadPlayheadRef = useRef(onReadPlayhead)
  onReadPlayheadRef.current = onReadPlayhead
  const onRewindForDubRef = useRef(onRewindForDub)
  onRewindForDubRef.current = onRewindForDub

  const recorder = useAudioRecorder({
    minSeconds: 1,
    maxSeconds: MAX_RECORD_SECONDS,
    countdownSeconds: 3,
    source: picker.source,
    ...picker.recorderProps,
    onRecorded: (file) => setTake(file),
    onTooShort: () => toast.error(t("timeline.recordingTooShort")),
    onPermissionDenied: (deniedSource) =>
      toast.error(
        deniedSource === "system" ? t("timeline.systemAudioCancelled") : t("timeline.recordingPermissionDenied"),
      ),
    onSystemAudioMissing: () => toast.error(t("timeline.systemAudioMissing")),
    onError: (message) => toast.error(message),
    onRecordingStarted: () => {
      dubLimitMsRef.current = null
      if (!playWithVideo) {
        dubStartMsRef.current = null
        return
      }
      onPlayForDub?.()
      dubStartMsRef.current = onReadPlayhead?.() ?? null
    },
  })

  useEffect(() => {
    if (!playWithVideo || recorder.phase !== "countdown") return
    onRewindForDubRef.current?.()
  }, [playWithVideo, recorder.phase])

  const locked = busy || recorder.isBusy

  const rememberDubLimit = useCallback(() => {
    if (!playWithVideo || dubStartMsRef.current == null || dubLimitMsRef.current != null) return
    const playhead = onReadPlayheadRef.current?.()
    if (playhead == null) return
    dubLimitMsRef.current = Math.max(0, Math.round(playhead - dubStartMsRef.current))
  }, [playWithVideo])

  useEffect(() => {
    const wasPlaying = shotWasPlayingRef.current
    shotWasPlayingRef.current = shotPlaying
    // Пауза перед отсчётом сюда не попадает: записи ещё нет. Ловим паузу и конец шота.
    if (!playWithVideo || !wasPlaying || shotPlaying || recorder.phase !== "recording") return
    rememberDubLimit()
    recorder.stop()
  }, [playWithVideo, recorder, rememberDubLimit, shotPlaying])

  return (
    <AudioComposeDialog
      title={t("timeline.record")}
      closeLabel={t("timeline.cancel")}
      busy={busy}
      portalTarget={portalTarget}
      closeOnBackdrop={false}
      onClose={() => {
        recorder.cancel()
        onClose()
      }}
    >
      {take ? null : (
        <div className="flex flex-col items-center gap-2 py-2">
          <div className="flex h-24 w-24 items-center justify-center">
            {recorder.phase === "countdown" ? (
              <span className="text-4xl font-semibold text-red-500 tabular-nums">{recorder.countdownLeft}</span>
            ) : recorder.phase === "recording" ? (
              <MicFillIcon stream={recorder.stream} className="h-full w-full text-red-500" />
            ) : (
              <Mic className="h-full w-full text-ui-text-muted" aria-hidden />
            )}
          </div>
          <span
            className={cn(
              "text-xs leading-4 text-ui-text-secondary tabular-nums",
              recorder.phase === "recording" ? null : "invisible",
            )}
            aria-hidden={recorder.phase !== "recording"}
          >
            {formatClock(recorder.phase === "recording" ? recorder.elapsedSeconds : 0)} /{" "}
            {formatClock(MAX_RECORD_SECONDS)}
          </span>
        </div>
      )}

      {take && takeUrl ? (
        <AudioTakeTimeline
          src={takeUrl}
          file={take}
          maxDurationMs={dubLimitMsRef.current}
          videoSync={playWithVideo ? (shotSync ?? null) : null}
        />
      ) : null}

      {take ? null : (
        <SelectDropdown
          value={picker.value}
          onChange={picker.select}
          options={inputOptions}
          disabled={locked}
          buttonClassName={AUDIO_COMPOSE_FIELD}
          dropdownDirection="down"
          ariaLabel={t("timeline.audioInput")}
        />
      )}

      {stsAvailable ? (
        <VoicePicker
          projectId={projectId}
          value={voice}
          onChange={setVoice}
          characterVoices={voices}
          providerKinds={stsProviderKinds}
          unavailableHint={t("timeline.voiceChangeElevenLabsOnly")}
          noneOptionLabel={t("timeline.keepOriginalVoice")}
          disabled={locked}
          className="w-full"
          buttonClassName={AUDIO_COMPOSE_FIELD}
          dropdownDirection="down"
          ariaLabel={t("timeline.voice")}
        />
      ) : null}

      {take ? null : (
        <label className="flex cursor-pointer items-center gap-2 text-xs leading-4 text-ui-text-secondary">
          <Checkbox
            checked={playWithVideo}
            disabled={locked}
            onChange={(event) => {
              const next = event.target.checked
              setPlayWithVideo(next)
              if (!next) onReleaseDubSilence?.()
            }}
            className="shrink-0 disabled:opacity-40"
          />
          <span>{t("timeline.playVideoWithRecording")}</span>
        </label>
      )}

      {take ? (
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => setTake(null)}>
            {t("timeline.recordAgain")}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={() => {
              void onSave(take, voice, dubLimitMsRef.current).then((ok) => {
                if (ok) onClose()
              })
            }}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {t("common.save")}
          </Button>
        </div>
      ) : recorder.phase === "idle" ? (
        <Button
          type="button"
          size="sm"
          title={t("timeline.startRecording")}
          disabled={busy}
          onClick={() => {
            if (playWithVideo) onPauseForDub?.()
            else onReleaseDubSilence?.()
            void recorder.start()
          }}
          className="w-full bg-red-600 text-white hover:bg-red-500"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MediaRecordGlyph recording={false} />}
          {t("timeline.rec")}
        </Button>
      ) : recorder.phase === "countdown" ? (
        <div className="h-8 w-full" aria-hidden />
      ) : (
        <Button
          type="button"
          size="sm"
          title={t("timeline.stopRecording")}
          onClick={() => {
            rememberDubLimit()
            recorder.stop()
          }}
          className="w-full animate-pulse bg-red-600 text-white hover:bg-red-500"
        >
          <MediaRecordGlyph recording />
          {formatClock(recorder.elapsedSeconds)}
        </Button>
      )}
    </AudioComposeDialog>
  )
}
